import { uniqueId } from 'lodash'
import React, { FC, useMemo, useRef, useState } from 'react'
import { secToHourMinute } from '../../../../../utils/date'

interface TipDetail {
  display: boolean
  biz?: number
  time?: number
  link: string
}

interface useMember {
  id: number
  name: string
  profile_image: string | null
  controlled: boolean
}

interface MemberTipDetail {
  display: boolean
  members?: useMember[]
  link: string
}

interface File {
  title: string
  use_time: number
  use_biz?: number
  switch_count?: number
  use_member_ids?: number[]
  use_members: useMember[]
}

interface Props {
  target: string
  type: string
  maxValue: number
  files: File[]
  startDate: string
  endDate: string
  id: number
  name: string
  searchModalOpenCallback: (any: any) => void
}

const MonthlyProcessGraph: FC<Props> = (props) => {
  const {
    target,
    type,
    maxValue,
    files,
    startDate,
    endDate,
    id,
    name,
    searchModalOpenCallback,
  } = props

  const tipRef = useRef() as React.MutableRefObject<HTMLInputElement>
  const memberTipRef = useRef() as React.MutableRefObject<HTMLInputElement>
  const graphRef = useRef() as React.MutableRefObject<HTMLInputElement>

  const tipInit:TipDetail = {
    display: false,
    biz: 0,
    time: 0,
    link: "",
  }
  const [tipDetail, setTipDetail] = useState<TipDetail>(tipInit)

  const memberTipInit:MemberTipDetail = {
    display: false,
    members: [],
    link: "",
  }
  const [memberTipDetail, setMemberTipDetail] = useState<MemberTipDetail>(memberTipInit)

  const displayTip = (event: any, link: string, biz?: number, time?: number) => {
    if (!tipRef.current) return
    const clientRect = graphRef.current.getBoundingClientRect()
    const x = event.pageX - (clientRect.left + window.pageXOffset)
    const y = event.pageY - (clientRect.top + window.pageYOffset)
    tipRef.current.style.top = `${y - 80}px`
    tipRef.current.style.left = `${x}px`
    setTipDetail({
      ...tipDetail,
      display: true,
      biz: biz,
      time: time,
      link: link,
    })
  }

  const hiddenTips = () => {
    if (!tipRef.current || !memberTipRef.current) return
    tipRef.current.style.top = "0px"
    tipRef.current.style.left = "0px"
    setTipDetail({
      ...tipDetail,
      display: false,
    })
    memberTipRef.current.style.top = "0px"
    memberTipRef.current.style.left = "0px"
    setMemberTipDetail({
      ...memberTipDetail,
      display: false,
    })
  }

  const displayMemberTip = (event: any, link: string, members?: useMember[]) => {
    if (!memberTipRef.current) return
    const clientRect = graphRef.current.getBoundingClientRect()
    const x = event.pageX - (clientRect.left + window.pageXOffset)
    const y = event.pageY - (clientRect.top + window.pageYOffset)
    memberTipRef.current.style.top = `${y - 10}px`
    memberTipRef.current.style.left = `${x}px`
    setMemberTipDetail({
      ...memberTipDetail,
      display: true,
      members: members,
      link: link,
    })
  }

  const calcValue = (file: File) => {
    if (type === "mismatch") {
      return (file.use_time / maxValue) * 100
    }
    return (((file.use_biz || 1) * (file.switch_count || 1) * (file.use_member_ids?.length || 1)) / maxValue) * 100
  }

  const title = useMemo(() => {
    if (type === "mismatch") {
      return "アプリ名"
    }
    return "ファイル名"
  }, [])

  const description = useMemo(() => {
    if (type === "mismatch") {
      return "利用時間"
    }
    if (target === "team") {
      return "Biz × 表示回数 × 作業人数"
    }
    return "Biz × 表示回数"
  }, [])

  const memberDisplay = (members: useMember[]) => {
    const num = members?.length > 3 ? 3 : members?.length
    const items = []
    for (let i = 0;i < num;i += 1) {
      items.push(
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            backgroundColor: "#EEE",
            position: "absolute",
            left: `${i * 15}%`,
          }}
          key={`member_display_${members[i].name}`}
        >
          {
            members[i].profile_image ? (
              <img
                alt=""
                src={members[i].profile_image || ""}
                style={{ width: "100%", borderRadius: "50%" }}
              />
            ) : (
              <img
                alt=""
                src={`/static/images/profile/${(members[i].id || 0) % 12}.jpeg`}
                style={{ width: "100%", borderRadius: "50%" }}
              />
            )
          }
        </div>,
      )
    }
    return items
  }

  return (
    <>
      {
        files.length ? (
          <>
            <div ref={graphRef}>
              <div className="header_area">
                <div
                  style={{
                    minWidth: "200px",
                  }}
                >
                  {title}
                </div>
                {
                  target === "team" && (
                    <div
                      style={{
                        minWidth: "200px",
                      }}
                    >
                      作業人数
                    </div>
                  )
                }
                <div
                  style={{
                    minWidth: `calc(100% - ${target === "team" ? "400px" : "200px"})`,
                    textAlign: "center",
                  }}
                >
                  {description}
                </div>
              </div>
              <div className="detail_area">
                <div
                  className="ruler_area"
                  style={{
                    minWidth: `calc(100% - ${target === "team" ? "400px" : "200px"})`,
                    marginLeft: target === "team" ? "400px" : "200px",
                  }}
                >
                  {
                    [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((value) => (
                      <div
                        className="ruler"
                        style={{
                          left: `${value}%`,
                        }}
                        key={`files_border_${type}_${value}`}
                      />
                    ))
                  }
                </div>
                {
                  maxValue && files.map((file) => (
                    <div
                      className="row"
                      key={`files_${type}_${file.title}`}
                    >
                      <div className="title_area no_trans">
                        {file.title}
                      </div>
                      {
                        target === "team" && (
                          <div
                            className="picture_area"
                            role="none"
                            onClick={
                              (event) => displayMemberTip(
                                event,
                                file.title,
                                file.use_members,
                              )
                            }
                          >
                            {memberDisplay(file.use_members || [])}
                            <div
                              style={{
                                position: "absolute",
                                right: "33%",
                              }}
                            >
                              {file.use_members?.length}
                              人
                            </div>
                          </div>
                        )
                      }
                      <div
                        className="graph_wrap"
                        style={{
                          minWidth: `calc(100% - ${target === "team" ? "400px" : "200px"})`,
                        }}
                      >
                        <div
                          className="graph"
                          style={{
                            width: `${calcValue(file)}%`,
                          }}
                          role="none"
                          onClick={
                            (event) => displayTip(
                              event,
                              file.title,
                              file.use_biz,
                              file.use_time,
                            )
                          }
                        />
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
            <div
              ref={tipRef}
              className="tip_wrap"
              style={{
                display: tipDetail.display ? "block" : "none",
              }}
            >
              <div className="tip">
                <div className="title no_trans">
                  {tipDetail.link}
                </div>
                {
                  tipDetail.biz && (
                    <div className="value">
                      Biz:
                      {tipDetail.biz}
                      biz
                    </div>
                  )
                }
                {
                  tipDetail.time && (
                    <div className="value">
                      作業時間:
                      {secToHourMinute(tipDetail.time)}
                    </div>
                  )
                }
                {
                  tipDetail.link && (
                    <>
                      {
                        target === "team" && type === "mismatch" && (
                          <button
                            data-gtm-click={`process - ${type} - button`}
                            type="button"
                            className="view"
                            onClick={() => searchModalOpenCallback(
                              {
                                startDate: startDate,
                                endDate: endDate,
                                teamIds: [id],
                                teamNames: [name],
                                memberIds: [],
                                memberNames: [],
                                keywords: [],
                                serviceNames: [tipDetail.link],
                                categoryIds: [],
                                categoryNames: [],
                                efficiencyLevel: [],
                                stressLevel: [],
                                focusLevel: [],
                              },
                            )}
                          >
                            作業を見る
                          </button>
                        )
                      }
                      {
                        target === "team" && type !== "mismatch" && (
                          <button
                            data-gtm-click={`process - ${type} - button`}
                            type="button"
                            className="view"
                            onClick={() => searchModalOpenCallback(
                              {
                                startDate: startDate,
                                endDate: endDate,
                                teamIds: [id],
                                teamNames: [name],
                                memberIds: [],
                                memberNames: [],
                                keywords: [`"${tipDetail.link}"`],
                                serviceNames: [],
                                categoryIds: [],
                                categoryNames: [],
                                efficiencyLevel: [],
                                stressLevel: [],
                                focusLevel: [],
                              },
                            )}
                          >
                            作業を見る
                          </button>
                        )
                      }
                      {
                        target === "member" && type === "mismatch" && (
                          <button
                            data-gtm-click={`process - ${type} - button`}
                            type="button"
                            className="view"
                            onClick={() => searchModalOpenCallback(
                              {
                                startDate: startDate,
                                endDate: endDate,
                                teamIds: [],
                                teamNames: [],
                                memberIds: [id],
                                memberNames: [name],
                                keywords: [],
                                serviceNames: [tipDetail.link],
                                categoryIds: [],
                                categoryNames: [],
                                efficiencyLevel: [],
                                stressLevel: [],
                                focusLevel: [],
                              },
                            )}
                          >
                            作業を見る
                          </button>
                        )
                      }
                      {
                        target === "member" && type !== "mismatch" && (
                          <button
                            data-gtm-click={`process - ${type} - button`}
                            type="button"
                            className="view"
                            onClick={() => searchModalOpenCallback(
                              {
                                startDate: startDate,
                                endDate: endDate,
                                teamIds: [],
                                teamNames: [],
                                memberIds: [id],
                                memberNames: [name],
                                keywords: [`"${tipDetail.link}"`],
                                serviceNames: [],
                                categoryIds: [],
                                categoryNames: [],
                                efficiencyLevel: [],
                                stressLevel: [],
                                focusLevel: [],
                              },
                            )}
                          >
                            作業を見る
                          </button>
                        )
                      }
                    </>
                  )
                }
                <button
                  type="button"
                  style={{
                    position: "absolute",
                    top: "0px",
                    right: "-4px",
                    lineHeight: "1.2em",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0",
                  }}
                  onClick={() => hiddenTips()}
                >
                  ×
                </button>
              </div>
            </div>
            <div
              ref={memberTipRef}
              className="member_tip_wrap"
              style={{
                display: memberTipDetail.display ? "block" : "none",
              }}
            >
              <div className="tip">
                <div className="title no_trans">
                  {memberTipDetail.link}
                </div>
                {
                  memberTipDetail.members?.map((member) => (
                    <div
                      className="personal"
                      key={uniqueId()}
                    >
                      <div className="icon">
                        {
                          member.profile_image ? (
                            <img
                              alt=""
                              src={member.profile_image || ""}
                            />
                          ) : (
                            <img
                              alt=""
                              src={`/static/images/profile/${(member.id || 0) % 12}.jpeg`}
                            />
                          )
                        }
                      </div>
                      <div className="name no_trans">
                        {member.name}
                      </div>
                      {
                        member.controlled && type === "mismatch" && (
                          <button
                            data-gtm-click={`process - ${type} - member_button`}
                            type="button"
                            className="view"
                            onClick={() => searchModalOpenCallback(
                              {
                                startDate: startDate,
                                endDate: endDate,
                                teamIds: [],
                                teamNames: [],
                                memberIds: [member.id],
                                memberNames: [member.name],
                                keywords: [],
                                serviceNames: [memberTipDetail.link],
                                categoryIds: [],
                                categoryNames: [],
                                efficiencyLevel: [],
                                stressLevel: [],
                                focusLevel: [],
                              },
                            )}
                          >
                            作業を見る
                          </button>
                        )
                      }
                      {
                        member.controlled && type !== "mismatch" && (
                          <button
                            data-gtm-click={`process - ${type} - member_button`}
                            type="button"
                            className="view"
                            onClick={() => searchModalOpenCallback(
                              {
                                startDate: startDate,
                                endDate: endDate,
                                teamIds: [],
                                teamNames: [],
                                memberIds: [member.id],
                                memberNames: [member.name],
                                keywords: [`"${memberTipDetail.link}"`],
                                serviceNames: [],
                                categoryIds: [],
                                categoryNames: [],
                                efficiencyLevel: [],
                                stressLevel: [],
                                focusLevel: [],
                              },
                            )}
                          >
                            作業を見る
                          </button>
                        )
                      }
                    </div>
                  ))
                }
                <button
                  type="button"
                  style={{
                    position: "absolute",
                    top: "0px",
                    right: "-4px",
                    lineHeight: "1.2em",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0",
                  }}
                  onClick={() => hiddenTips()}
                >
                  ×
                </button>
              </div>
            </div>
            <div
              className="close_process_screen"
              style={{
                display: (tipDetail.display || memberTipDetail.display) ? "block" : "none",
              }}
              role="none"
              onClick={() => hiddenTips()}
            />
          </>
        ) : (
          <>
            該当するデータはありませんでした
          </>
        )
      }
    </>
  )
}

export default MonthlyProcessGraph
